<template>
  <v-dialog
    v-model="dialog"
    persistent
    scrollable
    :disabled="isDisabled"
    :fullscreen="$vuetify.display.xs"
    :width="$vuetify.display.smAndUp ? 600 : 'auto'"
    style="z-index: 1100 !important;"
  >
    <template v-slot:activator="{ props }">
      <div v-bind="props" data-cy="list-podNodeItem-actions-updateTeamonly-activator">
        <slot></slot>
      </div>
    </template>

    <v-card v-if="waitingForUsersCount">
      <v-card-title class="d-flex justify-space-between">
        <span></span>

        <v-btn icon @click="dialog = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text>
        <div v-if="errorMessage" class="bg-error">
          {{ errorMessage }}
        </div>
        <div v-if="errorTranslatable" class="bg-error">
          {{ $replaceT(errorTranslatable.code, errorTranslatable.params, errorTranslatable.content) }}
        </div>
        <div class="text-center pt-5" v-else>
          <v-progress-circular
            class="float-left"
            indeterminate
            color="primary"
            :aria-label="$t('routes.node.loading')">
          </v-progress-circular>
          <h3 class="pl-2 mt-2 float-left">{{ $t('routes.node.loading') }}</h3>
        </div>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn variant="text" @click="cancel" data-cy="dialog-deleteNode-false">{{ $t('common.close') }}</v-btn>
      </v-card-actions>
    </v-card>

    <v-card v-if="noThingsSelected && shouldShowNoItems" data-cy="dialog-updateTeamonlyNoFiles-modal">
      <v-card-title class="d-flex justify-space-between">
        {{ $t('routes.node.selectFilesOrFolders') }}

        <v-btn icon @click="dialog = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn variant="text" @click="dialog = false" data-cy="dialog-updateTeamonlyNoFiles-false">{{ $t('common.close') }}</v-btn>
      </v-card-actions>
    </v-card>

    <v-card v-if="!waitingForUsersCount && !noThingsSelected" data-cy="dialog-updateTeamonlyItems-modal">
      <v-card-title class="d-flex justify-space-between" data-cy="dialog-updateTeamonlyItems-title">
        {{ $t('routes.node.titleTeamonlyUpdate') }}

        <v-btn icon @click="dialog = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pt-5">
        {{ actionType === 'remove' ? $t('routes.node.confirmRemoveTeamonlyAccess') : $t('routes.node.confirmSetTeamonlyAccess') }}
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          v-if="!errorMessage && !errorTranslatable"
          @click="updateItems"
          variant="flat"
          color="primary"
          data-cy="dialog-updateTeamonlyItems-ok"
          :disabled="updatingInProgress"
        >{{ actionType === 'remove' ? $t('routes.node.removeAccess') : $t('routes.node.setAccess') }}</v-btn>

        <v-btn variant="text" @click="cancel" data-cy="dialog-updateTeamonlyItems-false" class="btnCancel">{{ $t('common.cancel') }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { usePodStore } from '@/stores/PodStore.js';
import { useNodeStore } from '@/stores/NodeStore.js';
import { useCommonStore } from '@/stores/CommonStore.js';
import DialogActionKeys from '@/components/mixin/DialogActionKeys';

export default {
  emits: ['update:modelValue'],
  mixins: [DialogActionKeys],
  name: "UpdateTeamonlyDialog",
  props: {
    modelValue: {
      type: Boolean,
      default: false
    },
    nodes: {
      type: Array,
      required: true,
    },
    pods: {
      type: Array,
      required: true,
    },
    isDisabled: {
      type: Boolean,
      default: false
    },
    actionType: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      errorMessage: '',
      errorTranslatable: '',
      preventNoItems: false,
      waitingForUsersCount: false,
      userCount: 0,
      disabledUserCount: 0,
      updatingInProgress: false,
    }
  },
  methods: {
    cancel() {
      this.dialog = false;
      this.errorMessage = '';
      this.errorTranslatable = '';
      this.waitingForUsersCount = false;
      this.userCount = 0;
      this.disabledUserCount = 0;
    },
    updateItems() {
      this.errorMessage = '';
      this.errorTranslatable = '';
      this.preventNoItems = true;
      this.updatingInProgress = true;
      const promises = [];

      const regularNodeIds = [];
      for (let i = 0; i < this.nodes.length; i++) {
        if (this.nodes[i].parentid !== this.rootFolderId) {
          regularNodeIds.push(this.nodes[i].id);
        }
      }

      // Update team only access for nodes.
      if (regularNodeIds.length > 0) {
        const payload = {
          nodes: regularNodeIds,
          teamonly: this.actionType !== 'remove',
        };

        const nodeStore = useNodeStore();
        promises.push(nodeStore.updateTeamOnlyNodes(payload));
      }

      // Update team only access for pods.
      if (this.pods.length > 0) {
        const payload = {
          pods: this.pods.map((x) => x.id),
          teamonly: this.actionType !== 'remove',
        };

        const podStore = usePodStore();
        promises.push(podStore.updateTeamOnlyPods(payload));
      }

      Promise.all(promises)
        .then(() => {
          this.dialog = false;
          this.preventNoItems = true;

          this.$showMessage(this.$t('routes.node.teamonlyUpdateSuccess'));
        })
        .catch((error) => {
          this.preventNoItems = true;

          console.error(error);
          if (error?.response?.data?.error) {
            const errorMessage = this.$localizeMessage(error?.response?.data?.error, this.$t('common.generalError'));
            this.$showMessage(errorMessage, { type: 'error' });
          } else {
            console.log(error);
          }
        });
    },
  },
  watch: {
    dialog(newValue) {
      this.errorMessage = '';
      this.errorTranslatable = '';
      this.updatingInProgress = false;

      if (newValue) this.preventNoItems = false;
    },
    modelValue(val) {
      this.dialog = val;
    }
  },
  computed: {
    dialog: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit('update:modelValue', value);
      }
    },
    rootFolderId() {
      const commonStore = useCommonStore();
      return commonStore.rootFolderId;
    },
    shouldShowNoItems() {
      return !this.errorTranslatable && !this.errorMessage && !this.preventNoItems;
    },
    noThingsSelected() {
      return ((!this.nodes || this.nodes.length === 0) && (!this.pods || this.pods.length === 0));
    },
  }
}
</script>

<style>
</style>
